import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useState } from "react";

/**
 * Smart landing for brand-scoped sections (Rules, Content). If the user has
 * access to exactly one brand, navigate straight into the destination. If
 * more than one, show a picker. If zero, show a friendly message.
 */
export default function BrandPicker({ section, label }) {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/brands");
      setBrands(data);
      if (data.length === 1) {
        navigate(`/brands/${data[0].brand_id}/${section}`, { replace: true });
      } else {
        setLoaded(true);
      }
    })();
  }, [navigate, section]);

  if (!loaded) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <p className="text-[#6B6358]">Loading&hellip;</p>
      </div>
    );
  }

  if (brands.length === 0) {
    return (
      <div className="max-w-2xl">
        <p className="text-xs uppercase tracking-[0.22em] text-[#B5462F] mb-3">{label}</p>
        <h1 className="font-serif text-4xl tracking-tight">No brands available</h1>
        <p className="text-[#3D3A35] mt-4">
          Ask a platform administrator to give you access to a brand.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <p className="text-xs uppercase tracking-[0.22em] text-[#B5462F] mb-3">{label}</p>
      <h1 className="font-serif text-4xl tracking-tight">Pick a brand</h1>
      <p className="text-[#3D3A35] mt-3 mb-8">
        {label} is brand-scoped. Choose which brand you&rsquo;d like to work in.
      </p>
      <div className="border border-[#1A1F2C]/10 bg-white rounded-lg divide-y divide-[#1A1F2C]/10">
        {brands.map((b) => (
          <Link
            key={b.brand_id}
            to={`/brands/${b.brand_id}/${section}`}
            className="block px-6 py-4 hover:bg-[#F8F5EE]"
          >
            <div className="font-serif text-lg">{b.name}</div>
            {b.description && (
              <div className="text-sm text-[#6B6358] mt-1">{b.description}</div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
